import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Trophy, RotateCcw, Home, Book, X, Users, Wifi, WifiOff, Gem } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Currency Icons (matching App.js)
const EmeraldIcon = ({ className = "h-4 w-4" }) => (
  <Gem className={`${className} text-emerald-400 fill-emerald-400/20`} />
);

const RubyIcon = ({ className = "h-4 w-4" }) => (
  <Gem className={`${className} text-red-400 fill-red-400/20`} />
);

// Board dimensions
const ROWS = 6;
const COLS = 7;

// Create API instance - use the same baseURL pattern as the main app
const api = axios.create({
  baseURL: `${API_URL}/api`,  // Include /api prefix
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('authToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ============================================================
// FOUR IN A ROW GAME COMPONENT
// ============================================================
const FourInARowGame = ({ user, gameId: propGameId, onExit, isInStream = false, sharedChatStreamId = null }) => {
  const navigate = useNavigate();
  const { gameId: paramGameId } = useParams();
  const gameId = propGameId || paramGameId;
  
  // Game state
  const [game, setGame] = useState(null);
  const [board, setBoard] = useState(Array(COLS).fill(null).map(() => Array(ROWS).fill(0)));
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [myPlayerNum, setMyPlayerNum] = useState(null);
  const [winner, setWinner] = useState(null);
  const [winningCells, setWinningCells] = useState([]);
  const [isDraw, setIsDraw] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [players, setPlayers] = useState([]);
  
  // UI state
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [droppingPiece, setDroppingPiece] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [showRematchModal, setShowRematchModal] = useState(false);
  const [rematchRequested, setRematchRequested] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // WebSocket ref
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // ============================================================
  // WEBSOCKET CONNECTION
  // ============================================================
  const connectWebSocket = useCallback(() => {
    if (!gameId || !user?.id) return;
    
    const wsUrl = API_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const fullWsUrl = `${wsUrl}/api/ws/game/${gameId}?user_id=${user.id}`;
    
    console.log('🎮 4 In A Row: Connecting WebSocket...', fullWsUrl);
    
    try {
      wsRef.current = new WebSocket(fullWsUrl);
      
      wsRef.current.onopen = () => {
        console.log('✅ 4 In A Row WebSocket connected');
        setIsConnected(true);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('🎮 4 In A Row WS message:', data.type, data);
          handleWebSocketMessage(data);
        } catch (e) {
          console.error('Failed to parse WS message:', e);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('❌ 4 In A Row WebSocket error:', error);
      };
      
      wsRef.current.onclose = (event) => {
        console.log('🔌 4 In A Row WebSocket closed:', event.code);
        setIsConnected(false);
        
        // Reconnect if not clean close
        if (event.code !== 1000 && phase !== 'finished') {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 3000);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [gameId, user?.id, phase]);

  // Handle WebSocket messages
  const handleWebSocketMessage = useCallback((data) => {
    switch (data.type) {
      case 'game_start':
      case 'player_joined':
        // Refresh game state
        fetchGameState();
        break;
        
      case 'move_made':
        // Update board with the move
        if (data.board) {
          setBoard(data.board);
        }
        if (data.current_player) {
          setCurrentPlayer(data.current_player);
        }
        if (data.phase) {
          setPhase(data.phase);
        }
        if (data.winner !== undefined && data.winner !== null) {
          setWinner(data.winner);
          setWinningCells(data.winning_cells || []);
          setPhase('finished');
        }
        if (data.is_draw) {
          setIsDraw(true);
          setPhase('finished');
        }
        
        // Announce move in chat if in stream
        if (sharedChatStreamId && data.player_name) {
          // Could broadcast to chat here
        }
        break;
        
      case 'four_in_a_row_game_over':
        setWinner(data.winner);
        setWinningCells(data.winning_cells || []);
        setIsDraw(data.is_draw || false);
        setPhase('finished');
        break;
        
      case 'rematch_request':
        if (data.user_id !== user?.id) {
          setRematchRequested(true);
          setShowRematchModal(true);
        }
        break;
        
      case 'player_disconnected':
        toast.error(`${data.player_name || 'Opponent'} disconnected`);
        break;
        
      default:
        break;
    }
  }, [user?.id, sharedChatStreamId]);

  // ============================================================
  // FETCH GAME STATE
  // ============================================================
  const fetchGameState = useCallback(async () => {
    if (!gameId) return;
    
    try {
      const response = await api.get(`/four-in-a-row/${gameId}`);
      const gameData = response.data.game;
      
      setGame(gameData);
      setBoard(gameData.board || Array(COLS).fill(null).map(() => Array(ROWS).fill(0)));
      setCurrentPlayer(gameData.current_player || 1);
      setPhase(gameData.phase || 'lobby');
      setPlayers(gameData.players || []);
      setWinner(gameData.winner);
      setWinningCells(gameData.winning_cells || []);
      
      // Determine my player number
      const myPlayer = gameData.players?.find(p => p.id === user?.id);
      if (myPlayer) {
        setMyPlayerNum(myPlayer.player_num);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch game:', error);
      toast.error('Failed to load game');
      setLoading(false);
    }
  }, [gameId, user?.id]);

  // ============================================================
  // INITIALIZE
  // ============================================================
  useEffect(() => {
    if (gameId && user?.id) {
      fetchGameState();
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmount');
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [gameId, user?.id, fetchGameState, connectWebSocket]);

  // ============================================================
  // MAKE MOVE
  // ============================================================
  const handleColumnClick = async (column) => {
    // Validate it's my turn
    if (phase !== 'playing') {
      toast.error('Game is not in progress');
      return;
    }
    
    if (currentPlayer !== myPlayerNum) {
      toast.error("Not your turn!");
      return;
    }
    
    // Check column is not full
    if (board[column][ROWS - 1] !== 0) {
      toast.error('Column is full!');
      return;
    }
    
    // Find landing row for animation
    let landingRow = 0;
    for (let r = 0; r < ROWS; r++) {
      if (board[column][r] === 0) {
        landingRow = r;
        break;
      }
    }
    
    // Start drop animation
    setDroppingPiece({ column, player: myPlayerNum, targetRow: landingRow });
    
    try {
      const response = await api.post(`/four-in-a-row/${gameId}/move`, {
        column: column
      });
      
      // Update local state optimistically
      const newBoard = board.map(col => [...col]);
      newBoard[column][landingRow] = myPlayerNum;
      setBoard(newBoard);
      
      if (response.data.phase === 'finished') {
        setPhase('finished');
        setWinner(response.data.winner);
        setWinningCells(response.data.winning_cells || []);
        setIsDraw(response.data.is_draw || false);
      } else {
        setCurrentPlayer(response.data.current_player);
      }
      
    } catch (error) {
      console.error('Move failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to make move');
    } finally {
      setTimeout(() => setDroppingPiece(null), 300);
    }
  };

  // ============================================================
  // REMATCH
  // ============================================================
  const handleRematch = async () => {
    try {
      const response = await api.post(`/four-in-a-row/${gameId}/rematch`);
      const newGameId = response.data.new_game_id;
      
      toast.success('Rematch started!');
      setShowRematchModal(false);
      
      // Navigate to new game
      if (isInStream) {
        // Stay in stream, update game ID
        window.location.reload(); // Simple approach for stream games
      } else {
        navigate(`/four-in-a-row/${newGameId}`);
      }
    } catch (error) {
      console.error('Rematch failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to start rematch');
    }
  };

  const requestRematch = () => {
    // Send rematch request via WebSocket
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'rematch_request',
        user_id: user?.id
      }));
    }
    setRematchRequested(true);
    toast.info('Rematch request sent!');
  };

  // ============================================================
  // EXIT GAME
  // ============================================================
  const handleExit = () => {
    if (onExit) {
      onExit();
    } else {
      navigate('/games');
    }
  };

  // ============================================================
  // CHECK IF CELL IS WINNING CELL
  // ============================================================
  const isWinningCell = (col, row) => {
    return winningCells.some(([c, r]) => c === col && r === row);
  };

  // ============================================================
  // RENDER
  // ============================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-300">Loading game...</p>
        </div>
      </div>
    );
  }

  const player1 = players.find(p => p.player_num === 1);
  const player2 = players.find(p => p.player_num === 2);
  const isMyTurn = currentPlayer === myPlayerNum && phase === 'playing';
  const currentPlayerName = currentPlayer === 1 ? player1?.name : player2?.name;

  return (
    <div className={`${isInStream ? 'h-full' : 'min-h-screen'} bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col`}>
      {/* Header */}
      {!isInStream && (
        <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 p-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={handleExit} className="text-gray-400 hover:text-white">
                <Home className="h-5 w-5 mr-2" />
                Exit
              </Button>
              <h1 className="text-xl font-bold text-white">4 In A Row</h1>
            </div>
            <div className="flex items-center gap-3">
              {isConnected ? (
                <div className="flex items-center gap-1 text-green-400 text-sm">
                  <Wifi className="h-4 w-4" />
                  <span>Live</span>
                </div>
              ) : (
                <div className="flex items-center gap-1 text-red-400 text-sm">
                  <WifiOff className="h-4 w-4" />
                  <span>Reconnecting...</span>
                </div>
              )}
              <Button variant="ghost" size="sm" onClick={() => setShowRulesModal(true)} className="text-gray-400 hover:text-white">
                <Book className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Game Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 gap-6">
        {/* Player Info Bar */}
        <div className="flex items-center justify-between w-full max-w-lg">
          {/* Player 1 (Red) */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${currentPlayer === 1 && phase === 'playing' ? 'bg-red-500/20 ring-2 ring-red-500' : 'bg-gray-800/50'}`}>
            <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center shadow-lg shadow-red-500/30">
              {player1?.picture ? (
                <img src={player1.picture} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white font-bold">{player1?.name?.[0]?.toUpperCase() || '1'}</span>
              )}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{player1?.name || player1?.handle || 'Player 1'}</p>
              <p className="text-red-400 text-xs">Red</p>
            </div>
          </div>

          {/* VS / Turn Indicator */}
          <div className="text-center">
            {phase === 'playing' ? (
              <div className={`px-4 py-2 rounded-full ${isMyTurn ? 'bg-emerald-500 text-white' : 'bg-gray-700 text-gray-300'}`}>
                {isMyTurn ? "Your Turn!" : `${currentPlayerName}'s Turn`}
              </div>
            ) : phase === 'finished' ? (
              <div className="px-4 py-2 rounded-full bg-yellow-500 text-black font-bold">
                {winner === 0 || isDraw ? "Draw!" : winner === myPlayerNum ? "You Win!" : "You Lose"}
              </div>
            ) : (
              <div className="px-4 py-2 rounded-full bg-gray-700 text-gray-300">
                Waiting...
              </div>
            )}
          </div>

          {/* Player 2 (Green) */}
          <div className={`flex items-center gap-3 p-3 rounded-lg ${currentPlayer === 2 && phase === 'playing' ? 'bg-green-500/20 ring-2 ring-green-500' : 'bg-gray-800/50'}`}>
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center shadow-lg shadow-green-500/30">
              {player2?.picture ? (
                <img src={player2.picture} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white font-bold">{player2?.is_bot ? 'AI' : (player2?.name?.[0]?.toUpperCase() || '2')}</span>
              )}
            </div>
            <div>
              <p className="text-white font-medium text-sm">{player2?.name || player2?.handle || 'Waiting...'}</p>
              <p className="text-green-400 text-xs">Green</p>
            </div>
          </div>
        </div>

        {/* Wager Info */}
        {game?.wager_amount > 0 && (
          <div className="bg-yellow-500/20 text-yellow-400 px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
            <span className="flex items-center gap-1">Entry: {game.wager_amount} <EmeraldIcon className="h-4 w-4" /> each</span>
            <span>|</span>
            <span className="flex items-center gap-1">Prize Pool: {game.total_pot} <RubyIcon className="h-4 w-4" /></span>
          </div>
        )}

        {/* Game Board */}
        <div className="relative">
          {/* Column Hover Indicators */}
          <div className="absolute -top-12 left-0 right-0 flex">
            {Array(COLS).fill(null).map((_, col) => (
              <div
                key={`indicator-${col}`}
                className="flex-1 flex justify-center"
              >
                <AnimatePresence>
                  {hoveredColumn === col && isMyTurn && board[col][ROWS - 1] === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 0.7, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`w-10 h-10 rounded-full ${myPlayerNum === 1 ? 'bg-red-500' : 'bg-green-500'}`}
                    />
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Board Container */}
          <div className="bg-blue-600 p-3 rounded-xl shadow-2xl shadow-blue-500/20">
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}>
              {/* Render columns */}
              {Array(COLS).fill(null).map((_, col) => (
                <div
                  key={`col-${col}`}
                  className={`flex flex-col-reverse gap-2 cursor-pointer transition-transform ${isMyTurn && board[col][ROWS - 1] === 0 ? 'hover:scale-105' : ''}`}
                  onMouseEnter={() => setHoveredColumn(col)}
                  onMouseLeave={() => setHoveredColumn(null)}
                  onClick={() => handleColumnClick(col)}
                >
                  {Array(ROWS).fill(null).map((_, row) => {
                    const cellValue = board[col][row];
                    const isWinning = isWinningCell(col, row);
                    const isDropping = droppingPiece?.column === col && droppingPiece?.targetRow === row;
                    
                    return (
                      <motion.div
                        key={`cell-${col}-${row}`}
                        className={`w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-800 flex items-center justify-center relative overflow-hidden
                          ${isWinning ? 'ring-4 ring-yellow-400 ring-opacity-100' : ''}
                        `}
                        animate={isWinning ? { scale: [1, 1.1, 1] } : {}}
                        transition={{ repeat: isWinning ? Infinity : 0, duration: 0.5 }}
                      >
                        <AnimatePresence>
                          {cellValue !== 0 && (
                            <motion.div
                              initial={isDropping ? { y: -300, opacity: 1 } : { scale: 0.8, opacity: 0 }}
                              animate={{ y: 0, scale: 1, opacity: 1 }}
                              transition={{ 
                                type: isDropping ? "spring" : "tween",
                                stiffness: isDropping ? 200 : 300,
                                damping: isDropping ? 15 : 20,
                                duration: isDropping ? undefined : 0.2
                              }}
                              className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg
                                ${cellValue === 1 
                                  ? 'bg-gradient-to-br from-red-400 to-red-600 shadow-red-500/50' 
                                  : 'bg-gradient-to-br from-green-400 to-green-600 shadow-green-500/50'}
                                ${isWinning ? 'animate-pulse' : ''}
                              `}
                            />
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Game Over Actions */}
        {phase === 'finished' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-4"
          >
            <Button
              onClick={requestRematch}
              disabled={rematchRequested}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              {rematchRequested ? 'Waiting...' : 'Rematch'}
            </Button>
            <Button variant="outline" onClick={handleExit} className="border-gray-600 text-gray-300 hover:bg-gray-800">
              <Home className="h-5 w-5 mr-2" />
              Exit
            </Button>
          </motion.div>
        )}

        {/* Waiting for Player 2 */}
        {phase === 'lobby' && players.length < 2 && (
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Waiting for opponent to join...</p>
          </div>
        )}
        
        {/* Stats Card */}
        <div className="w-full max-w-lg">
          <div className="bg-gray-800/50 rounded-xl p-4 backdrop-blur-sm">
            <h4 className="text-sm font-medium text-gray-400 mb-3 text-center">Your Stats</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-green-400">{user?.wins || 0}</div>
                <div className="text-xs text-gray-400">Wins</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-red-400">{user?.losses || 0}</div>
                <div className="text-xs text-gray-400">Losses</div>
              </div>
              <div className="bg-gray-700/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-purple-400">{user?.streak || 0}</div>
                <div className="text-xs text-gray-400">Streak</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rules Modal */}
      <Dialog open={showRulesModal} onOpenChange={setShowRulesModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Book className="h-6 w-6 text-emerald-400" />
              4 In A Row Rules
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-gray-300">
            <div>
              <h4 className="font-semibold text-white mb-2">Objective</h4>
              <p>Be the first player to connect 4 of your pieces in a row!</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">How to Play</h4>
              <ul className="list-disc list-inside space-y-1">
                <li>Players take turns dropping pieces into columns</li>
                <li>Pieces fall to the lowest available slot</li>
                <li>Tap/click any column to drop your piece</li>
                <li>Player 1 (Red) always goes first</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Winning</h4>
              <p>Connect 4 pieces in any direction:</p>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="bg-gray-800 p-2 rounded text-center text-sm">
                  <span className="text-blue-400">Horizontal</span> ➡️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center text-sm">
                  <span className="text-blue-400">Vertical</span> ⬇️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center text-sm">
                  <span className="text-blue-400">Diagonal</span> ↗️
                </div>
                <div className="bg-gray-800 p-2 rounded text-center text-sm">
                  <span className="text-blue-400">Diagonal</span> ↘️
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-2">Draw</h4>
              <p>If the board fills up with no winner, the game is a draw and entries are refunded.</p>
            </div>
            
            <Button onClick={() => setShowRulesModal(false)} className="w-full bg-emerald-600 hover:bg-emerald-700">
              Got it!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rematch Modal */}
      <Dialog open={showRematchModal} onOpenChange={setShowRematchModal}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-emerald-400" />
              Rematch Request
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-gray-300">
              {rematchRequested ? 'Opponent wants a rematch!' : 'Send a rematch request?'}
            </p>
            <div className="flex gap-3">
              <Button onClick={handleRematch} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Accept
              </Button>
              <Button variant="outline" onClick={() => setShowRematchModal(false)} className="flex-1 border-gray-600">
                Decline
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FourInARowGame;
